import React, { useState } from 'react';
import { Calendar, Send, CheckCircle, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { mockBookAppointment, mockSubmitContact, mockTimeSlots } from '../mock';

const Contact = () => {
  const [activeTab, setActiveTab] = useState('appointment');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Get available time slots for selected date
  const getAvailableTimeSlots = () => {
    if (!selectedDate) return [];
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return mockTimeSlots.filter(slot => slot.date === dateStr && slot.available);
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }

    setIsSubmitting(true);
    
    const appointmentData = {
      ...appointmentForm,
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: selectedTime
    };

    try {
      const result = await mockBookAppointment(appointmentData);
      if (result.success) {
        toast.success(result.message);
        // Reset form
        setAppointmentForm({ name: '', email: '', phone: '', company: '', message: '' });
        setSelectedDate(null);
        setSelectedTime('');
      }
    } catch (error) {
      toast.error('Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await mockSubmitContact(contactForm);
      if (result.success) {
        toast.success(result.message);
        setContactForm({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      toast.error('Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-white relative">
      {/* Top separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-20 animate-fade-in">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                Contactez-nous
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              Prêt à démarrer votre transformation digitale ? Prenez rendez-vous ou envoyez-nous un message
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            {/* Left: Contact Info */}
            <div className="space-y-6">
              <Card className="border-2 border-orange-100 hover:shadow-xl transition-shadow duration-300">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold">Informations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-orange-500 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">contact@collado-ubeda.fr</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-orange-500 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Téléphone</p>
                      <p className="font-medium">+33 1 23 45 67 89</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-orange-500 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Adresse</p>
                      <p className="font-medium">Paris, France</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-orange-500 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Horaires</p>
                      <p className="font-medium">Lun-Ven: 9h-18h</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500 to-pink-500 text-white border-0">
                <CardContent className="pt-6">
                  <CheckCircle className="w-12 h-12 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Réponse rapide</h3>
                  <p className="text-white/90">
                    Nous vous répondons sous 24h pour discuter de vos besoins et vous proposer une solution adaptée.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right: Forms */}
            <div className="lg:col-span-2">
              {/* Tabs */}
              <div className="flex space-x-4 mb-6 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('appointment')}
                  className={`pb-3 px-4 font-medium transition-colors relative ${
                    activeTab === 'appointment'
                      ? 'text-orange-600'
                      : 'text-gray-600 hover:text-orange-500'
                  }`}
                >
                  <Calendar className="w-4 h-4 inline-block mr-2" />
                  Prendre rendez-vous
                  {activeTab === 'appointment' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`pb-3 px-4 font-medium transition-colors relative ${
                    activeTab === 'contact'
                      ? 'text-orange-600'
                      : 'text-gray-600 hover:text-orange-500'
                  }`}
                >
                  <Send className="w-4 h-4 inline-block mr-2" />
                  Message
                  {activeTab === 'contact' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></span>
                  )}
                </button>
              </div>

              {/* Appointment Form */}
              {activeTab === 'appointment' && (
                <Card className="border-2 border-orange-100">
                  <CardHeader>
                    <CardTitle>Réserver un créneau</CardTitle>
                    <CardDescription>
                      Choisissez une date et un horaire pour un rendez-vous personnalisé
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAppointmentSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nom complet *
                          </label>
                          <Input
                            required
                            value={appointmentForm.name}
                            onChange={(e) => setAppointmentForm({ ...appointmentForm, name: e.target.value })}
                            placeholder="Jean Dupont"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                          </label>
                          <Input
                            type="email"
                            required
                            value={appointmentForm.email}
                            onChange={(e) => setAppointmentForm({ ...appointmentForm, email: e.target.value })}
                            placeholder="jean@exemple.fr"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Téléphone *
                          </label>
                          <Input
                            required
                            value={appointmentForm.phone}
                            onChange={(e) => setAppointmentForm({ ...appointmentForm, phone: e.target.value })}
                            placeholder="+33 6 12 34 56 78"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Entreprise
                          </label>
                          <Input
                            value={appointmentForm.company}
                            onChange={(e) => setAppointmentForm({ ...appointmentForm, company: e.target.value })}
                            placeholder="Votre entreprise"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date *
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal"
                              >
                                <Calendar className="mr-2 h-4 w-4" />
                                {selectedDate ? format(selectedDate, 'PPP', { locale: fr }) : 'Choisir une date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <CalendarComponent
                                mode="single"
                                selected={selectedDate}
                                onSelect={setSelectedDate}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Heure *
                          </label>
                          <select
                            required
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            disabled={!selectedDate}
                          >
                            <option value="">Sélectionner une heure</option>
                            {getAvailableTimeSlots().map((slot) => (
                              <option key={slot.id} value={slot.time}>
                                {slot.time}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Message
                        </label>
                        <Textarea
                          value={appointmentForm.message}
                          onChange={(e) => setAppointmentForm({ ...appointmentForm, message: e.target.value })}
                          placeholder="Décrivez brièvement vos besoins..."
                          rows={4}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                      >
                        {isSubmitting ? 'Envoi...' : 'Confirmer le rendez-vous'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Contact Form */}
              {activeTab === 'contact' && (
                <Card className="border-2 border-orange-100">
                  <CardHeader>
                    <CardTitle>Envoyez-nous un message</CardTitle>
                    <CardDescription>
                      Nous vous répondrons dans les plus brefs délais
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nom complet *
                          </label>
                          <Input
                            required
                            value={contactForm.name}
                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                            placeholder="Jean Dupont"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                          </label>
                          <Input
                            type="email"
                            required
                            value={contactForm.email}
                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                            placeholder="jean@exemple.fr"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sujet *
                        </label>
                        <Input
                          required
                          value={contactForm.subject}
                          onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                          placeholder="Demande de renseignements"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Message *
                        </label>
                        <Textarea
                          required
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          placeholder="Parlez-nous de votre projet..."
                          rows={6}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                      >
                        {isSubmitting ? 'Envoi...' : 'Envoyer le message'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Breathing space before footer */}
      <div className="h-16"></div>
    </section>
  );
};

export default Contact;
